import { batch, observable } from "@legendapp/state";
import { persistObservable } from "@legendapp/state/persist";
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage";

// ---------------------------------------------------------------------------
// Liked chapters (persisted locally, synced to backend by callers)
// ---------------------------------------------------------------------------

export const likedChapters$ = observable<Record<number, boolean>>({});

persistObservable(likedChapters$, {
  local: "feed_likedChapters",
  pluginLocal: ObservablePersistLocalStorage,
});

export function setChapterLiked(chapterId: number, liked: boolean): void {
  batch(() => {
    // Store an explicit boolean (never delete). Matches iOS, which sets
    // feedStore$.likedChapterIDs[chapterID] to a boolean and never removes the
    // key — so an intentional unlike survives the next server-state seed/merge
    // (the seed guards only fill keys whose peek() === undefined).
    likedChapters$[chapterId].set(liked);
  });
}

// ---------------------------------------------------------------------------
// Study time tracking
// ---------------------------------------------------------------------------

export const totalElapsedSeconds$ = observable(0);
export const dailyElapsedSeconds$ = observable(0);
export const dailyElapsedDate$ = observable("");

persistObservable(totalElapsedSeconds$, {
  local: "feed_totalElapsedSeconds",
  pluginLocal: ObservablePersistLocalStorage,
});

persistObservable(dailyElapsedSeconds$, {
  local: "feed_dailyElapsedSeconds",
  pluginLocal: ObservablePersistLocalStorage,
});

persistObservable(dailyElapsedDate$, {
  local: "feed_dailyElapsedDate",
  pluginLocal: ObservablePersistLocalStorage,
});

function getTodayDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

// Reset stale daily counter
{
  const today = getTodayDateString();
  if (dailyElapsedDate$.get() !== today) {
    dailyElapsedDate$.set(today);
    dailyElapsedSeconds$.set(0);
  }
}

export function incrementElapsedTime(): void {
  const today = getTodayDateString();
  if (dailyElapsedDate$.get() !== today) {
    dailyElapsedDate$.set(today);
    dailyElapsedSeconds$.set(0);
  }
  totalElapsedSeconds$.set((s) => s + 1);
  dailyElapsedSeconds$.set((s) => s + 1);
}

// ---------------------------------------------------------------------------
// Playback speed
// ---------------------------------------------------------------------------

export const playbackRate$ = observable(1.0);

persistObservable(playbackRate$, {
  local: "feed_playbackRate",
  pluginLocal: ObservablePersistLocalStorage,
});

// iOS speed cycle (VideoScreenViewController.swift:112) — note there is no 1.5x
// or 2x; tapping cycles 1 → 0.75 → 0.5 → 1.25 → back to 1.
const SPEED_CYCLE = [1.0, 0.75, 0.5, 1.25];

// Migrate a persisted speed from an older build (which offered 1.5x/2x) back to
// 1x, so a stale value can't show a label/rate that's no longer in the cycle.
if (!SPEED_CYCLE.includes(playbackRate$.peek())) {
  playbackRate$.set(1.0);
}

export function cycleSpeed(): void {
  playbackRate$.set((prev) => {
    const i = SPEED_CYCLE.indexOf(prev);
    return SPEED_CYCLE[(i + 1) % SPEED_CYCLE.length];
  });
}
